import { useEffect, useState, useRef, lazy } from 'react';
import { Suspense } from 'react';
import { Row, Col, ListGroup, ListGroupItem } from 'reactstrap';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  TIPO_ITENS,
  ADDONS,
  STATUS_ETAPA_STAGEGATE,
} from 'src/utils/constants';
import Panel from 'src/componentes/panel';
import PanelCollapse from 'src/componentes/panel-collapse';
import ContentManager from 'src/componentes/content-manager';
import LoadingScreen from 'src/componentes/LoadingScreen';
import Stepper from 'src/components/Stepper';
import { capitalizeFirstLetter } from 'src/utils/string';
import useAuth from 'src/hooks/useAuth';
import ContextMenuIcon from 'src/componentes/context_menu_icon';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import EmbedPowerBI from 'src/paginas/power-bi/componentes/power-bi-dialog';
import {
  getTipoEntidadeComentario,
  buildComentarioDialogTitle,
} from 'src/componentes/comentarios/utils';
import Mensagem from 'src/paginas/mensagem/modal';
import MovimentacaoFinanceiraListView from 'src/paginas/MovimentacaoFinanceira';
import GraficoMovimentacaoFinanceira from 'src/paginas/MovimentacaoFinanceira/grafico';
import history from 'src/history';
import FichaProjeto from 'src/paginas/projeto/ficha';
import { openComentarioModal } from 'src/componentes/comentarios/ComentarioButton';
import ResumoStageGate from '../Resumo';

const PMCanvasFullscreen = lazy(() =>
  import('src/paginas/item/edit/canvas/PMCanvasFullscreen')
);
const Consolidacao = lazy(() => import('src/paginas/item/edit/consolidacao'));
const Permissoes = lazy(() => import('src/paginas/item/edit/permissao'));
const Configuracao = lazy(() => import('src/paginas/item/edit/configuracao'));
const Historico = lazy(() => import('src/paginas/versao-alteracao'));
const StatusReportList = lazy(() =>
  import('src/paginas/projeto/status-report')
);
const IndicadoresParametros = lazy(() =>
  import('src/paginas/projeto/valores/indicadores-parametros')
);
const TermoAbertura = lazy(() => import('src/paginas/item/edit/termoabertura'));
const CestaIndicadores = lazy(() =>
  import('src/paginas/item/edit/cestaindicadores')
);
const TermoEncerramento = lazy(() =>
  import('src/paginas/item/edit/termoencerramento')
);
const LicoesAprendidasList = lazy(() =>
  import('src/paginas/item/edit/termoencerramento/licoesaprendidas')
);

const StageGateEdit = ({ model, id, ...rest }) => {
  const intl = useIntl();
  const { terms: resources, addons } = useAuth();

  const [isEditable, setIsEditable] = useState(false);

  //referência do setter do Stepper
  const setActiveStepRef = useRef(null);

  const entityId = id || model?.getModel('id').value;
  const stageGate = model?.getModel('stageGate').value;
  const etapaAtualExistente = model?.getModel('etapaAtualStageGate').value;
  const etapaAtual =
    etapaAtualExistente ??
    (stageGate?.etapas?.length > 0 ? stageGate.etapas[0] : null);

  if (etapaAtualExistente == null && etapaAtual != null)
    model.getModel('etapaAtualStageGate').requestChange(etapaAtual);

  const etapas = stageGate?.etapas || [];
  const currentStageIndex =
    etapas.findIndex((e) => e.id === etapaAtual?.id) ?? 0;
  const [selectedIndex, setSelectedIndex] = useState(currentStageIndex);
  const selectedEtapa = etapas[selectedIndex];
  const projetoExistente = entityId && entityId > 0;

  const fieldsConfig = [
    ...(JSON.parse(selectedEtapa?.camposEtapaJSON) || []),
    ...(JSON.parse(selectedEtapa?.atributosEtapaJSON) || []),
    ...(JSON.parse(selectedEtapa?.anexosEtapaJSON || '[]') || []),
  ];

  //Todos os anexos das etapas anteriores + etapa atual para exibição cumulativa dos anexos
  const allAnexosConfigs = [
    ...(JSON.parse(selectedEtapa?.anexosEtapaJSON || '[]') || []),
    ...Array.from({ length: selectedIndex }, (_, i) => etapas[i])
      .filter(Boolean)
      .flatMap((etapa) => JSON.parse(etapa.anexosEtapaJSON || '[]') || []),
  ];

  const shouldDisplayField = (fieldId) => {
    const fieldIdStr = fieldId.toString();

    // Verifica primeiro na etapa selecionada
    const config = fieldsConfig.find((c) => c.id.toString() === fieldIdStr);
    if (config?.utilizar === true) {
      return true;
    }

    // Verifica etapas anteriores para exibir os campos de forma cumulativa
    for (let i = 0; i < selectedIndex; i++) {
      const etapa = etapas[i];
      if (!etapa) continue;

      const prevFieldsConfig = [
        ...(JSON.parse(etapa.camposEtapaJSON || '[]') || []),
        ...(JSON.parse(etapa.atributosEtapaJSON || '[]') || []),
        ...(JSON.parse(etapa.anexosEtapaJSON || '[]') || []),
      ];

      const prevConfig = prevFieldsConfig.find(
        (c) => c.id.toString() === fieldIdStr
      );
      if (prevConfig?.utilizar === true) {
        return true;
      }
    }

    return false;
  };

  const isFieldRequired = (fieldId) => {
    const config = fieldsConfig.find(
      (c) => c.id.toString() === fieldId.toString()
    );
    return config?.obrigatorio === true;
  };

  //função que verifica se um campo pode ser editado ou não conforme a etapa a qual pertence
  const isFieldEditable = (fieldId) => {
    const config = fieldsConfig.find(
      (c) => c.id.toString() === fieldId.toString()
    );
    //Apenas campos da etapa atual poderão ser editados
    return config?.utilizar === true && isEditable;
  };

  const [activeTab, setActiveTab] = useState(1);
  const [canvasModalKey, setCanvasModalKey] = useState(null);
  const [permissions, setPermissions] = useState({
    allowEdit: false,
    allowDelete: false,
    allowView: false,
    allowViewPlanejamentoInicial: false,
  });
  const [canCreateLessons, setCanCreateLessons] = useState(false);

  const [isEditableAttributes, setIsEditableAttributes] = useState(false);

  const TABS = {
    RESUMO: 1,
    CONSOLIDACAO: 3,
    PERMISSOES: 4,
    CONFIGURACOES: 5,
    HISTORICO: 6,
    STATUSREPORT: 7,
    INDICADORESEPARAMETROS: 9,
    TERMOABERTURA: 10,
    CESTAINDICADORES: 11,
    TERMOENCERRAMENTO: 12,
    MOVIMENTACAOFINANCEIRA: 13,
    LICOES_APRENDIDAS: 14,
  };

  const permitirDigitacaoValores =
    model.getModel('configuracaoGeral').value &&
    model.getModel('configuracaoGeral').value.permitirDigitacaoValores;

  const ehProjetoConcluidoCancelado =
    model.getModel('status') &&
    model.getModel('status').value &&
    (model.getModel('status').value.id == 3 ||
      model.getModel('status').value.id == 5);

  const possuiAddonContratosConvenios =
    addons?.find((x) => x.id === ADDONS.CONTRATOS_CONVENIOS && x.ativo) != null;

  let utilizaAnaliseStakeholders =
    model.getModel('configuracaoGeral').value &&
    model.getModel('configuracaoGeral').value.utilizarAnaliseStakeholders;

  const exibirPowerBI = permissions?.allowViewPowerBI !== false;

  const primeiraSecaoAberta = 'iniciacao';

  const getPermissions = (id) => {
    http
      .post(`/Item/GetPermissoesPorId`, {
        id: id,
      })
      .then((response) => {
        if (response.data != null) {
          setPermissions({ ...response.data });
        }
      });
  };

  const checkCanCreateLessons = (itemId) => {
    if (!itemId) {
      setCanCreateLessons(false);
      return;
    }

    http
      .get(`/ItemLicaoAprendidaTermoEncerramento/CanCreate`, {
        params: { itemId: itemId },
      })
      .then((response) => {
        if (response.data != null) {
          setCanCreateLessons(response.data.canCreate || false);
        }
      })
      .catch(() => {
        setCanCreateLessons(false);
      });
  };

  useEffect(() => {
    if (projetoExistente) {
      getPermissions(id);

      // Verificar permissão para criar lições aprendidas
      if (entityId) {
        checkCanCreateLessons(entityId);
      }
    }
  }, [entityId]);

  useEffect(() => {
    const etapaAtualNoModel = model.getModel('etapaExibidaStageGate').value;

    if (selectedEtapa?.id !== etapaAtualNoModel?.id) {
      model.getModel('etapaExibidaStageGate').requestChange(selectedEtapa);
    }
  }, [selectedEtapa?.id, model]);

  //sincroniza selectedIndex quando etapaAtualExistente muda
  useEffect(() => {
    if (!etapas?.length) return;

    const newIndex = etapas.findIndex((e) => e.id === etapaAtualExistente?.id);
    if (newIndex >= 0 && newIndex !== selectedIndex) {
      setSelectedIndex(newIndex);
      if (setActiveStepRef.current) {
        setActiveStepRef.current(newIndex);
      }
    }
  }, [etapaAtualExistente?.id]);

  useEffect(() => {
    const finalized = stageGate?.stageGateFinalizado === true;
    const hasPendingStatus =
      etapaAtual?.statusEtapa?.id === STATUS_ETAPA_STAGEGATE.PENDENTE;
    const isCurrent = selectedIndex === currentStageIndex;
    setIsEditable(finalized || (isCurrent && !hasPendingStatus));
    setIsEditableAttributes(projetoExistente && (finalized || isCurrent));
  }, [stageGate, etapaAtual, selectedIndex, currentStageIndex]);

  const handleCanvasSave = () => {
    if (model && model.getModel) {
      const forceRefreshModel = model.getModel('forceRefresh');
      if (forceRefreshModel) {
        forceRefreshModel.requestChange(Date.now());
      }
    }
  };

  const obterFiltroUrl = (item) => {
    let { filtro } = rest;

    let url = '';

    // Filtra o Id do Item
    url = `?itemId=${item.id}`;

    if (filtro != null && filtro != undefined) {
      url += `&openLastFilter=false`;

      // Tipo Filtro Data
      if (filtro.tipoFiltroData != null && filtro.tipoFiltroData.id > 0)
        url += `&tipoFiltroDataId=${filtro.tipoFiltroData.id}`;

      // Data Inicio
      if (filtro.dataInicio != null) url += `&dataInicio=${filtro.dataInicio}`;

      // Data Termino
      if (filtro.dataTermino != null)
        url += `&dataTermino=${filtro.dataTermino}`;
    }

    return url;
  };

  const openCanvasModal = () => {
    if (canvasModalKey) return;

    let modalKey = ContentManager.addContent(
      <PMCanvasFullscreen
        model={model}
        id={id}
        onClose={() => {
          setCanvasModalKey(null);
        }}
        onSave={() => {
          handleCanvasSave();
        }}
      />
    );

    setCanvasModalKey(modalKey);
  };

  const handleGetSetActive = (setter) => {
    setActiveStepRef.current = setter;
  };

  const handleTabAnaliseStakeholders = () => {
    history.push({
      pathname: '/gerenciar/projeto/analise-stakeholders',
      search: `?id=${id}`,
    });
  };

  const handleFichaClick = (item) => {
    ContentManager.addContent(<FichaProjeto id={item.id}></FichaProjeto>);
  };

  const handleTabEAP = () => {
    history.push({
      pathname: '/acompanhar/eap',
      search: `?itemId=${id}`,
    });
  };

  const handleMatrizResponsabilidadesClick = () => {
    history.push({
      pathname: '/acompanhar/matriz-responsabilidades',
      search: `?id=${id}`,
    });
  };

  const handlePlanoAcaoClick = (item) => {
    let url = obterFiltroUrl(item);

    history.push({
      pathname: '/gerenciar/gantt',
      search: url,
    });
  };

  const handleValueClick = (item) => {
    let url = obterFiltroUrl(item);

    history.push({
      pathname: '/gerenciar/valor',
      search: url,
    });

    rest.callback && rest.callback();
    rest.handleCloseModal && rest.handleCloseModal();
  };

  const handleRelacionamentosClick = (item) => {
    let url = obterFiltroUrl(item);

    history.push({
      pathname: '/gerenciar/arvore',
      search: url,
    });
  };

  const handleRelatorioBIClick = (item) => {
    http
      .post(`/PowerBI/ObterConfiguracoesPorIndicador`, {
        indicadorID: item.id,
      })
      .then((response) => {
        if (item.desdobramento) {
          response.data.model.campo = null;
        }
        ContentManager.addContent(
          <EmbedPowerBI
            itemId={item.id}
            relatorioPBI={response.data.model.relatorioPBI}
            embedURL={response.data.model.embedURL}
            workspacePBI={response.data.model.workspacePBI}
            filtros={response.data.model.filtros}
            embedToken={response.data.model.embedToken}
            usarAutenticacaoMicrosoft={
              response.data.model.usarAutenticacaoMicrosoft
            }
          />
        );
      })
      .catch((error) => {
        errorHandler(error);
      });
  };

  const handleComentarioClick = () => {
    const tipoId = TIPO_ITENS.PROJETO;
    const tipoEntidade = getTipoEntidadeComentario(tipoId);
    if (!tipoEntidade) return;

    const showTabs = false;

    openComentarioModal({
      tipoEntidade,
      entidadeId: entityId,
      quantidadeComentarios: 0,
      dialogTitle: buildComentarioDialogTitle(intl, '', null),
      item: {
        id: entityId,
        tipo: { id: tipoId },
        nome: '',
        periodicidade: model.getModel('periodicidade')?.value,
        dataInicioItem: model.getModel('dataInicioItem')?.value,
        dataTerminoItem: model.getModel('dataTerminoItem')?.value,
      },
      modalProps: {
        showTabs,
        dataInicio: null,
        dataTermino: null,
      },
    });
  };

  const handleMensagemClick = (item) => {
    ContentManager.addContent(<Mensagem fromItem item={{ id: item.id }} />);
  };

  const handelToggleTab = (tab) => {
    setActiveTab(tab);
  };

  const renderMenuItem = (
    text,
    onClick,
    isActive = false,
    showNewWindowIcon = false,
    origemClick = null,
    indentado = false,
    disabled = false
  ) => {
    const item = { id: entityId, tipo: { id: TIPO_ITENS.PROJETO } };

    const needsObterFiltroUrl = [
      'valores',
      'relacionamentos',
      'planoAcao',
      'eap',
    ].includes(origemClick);

    return (
      <ListGroupItem
        action
        className={isActive ? 'active' : ''}
        onClick={onClick}
        disabled={disabled}
        style={{
          cursor: 'pointer',
          border: 'none',
          paddingLeft: indentado ? '16px' : '10px',
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          {typeof text === 'string' ? (
            <p
              style={{
                overflowWrap: 'normal',
                textOverflow: 'ellipsis',
                overflowX: 'hidden',
                marginBottom: 'initial',
                flex: 1,
              }}
            >
              {text}
            </p>
          ) : (
            <div style={{ flex: 1 }}>{text}</div>
          )}
          {showNewWindowIcon && origemClick && (
            <div className="ml-2" onClick={(e) => e.stopPropagation()}>
              <ContextMenuIcon
                item={item}
                origemClick={origemClick}
                {...(needsObterFiltroUrl && {
                  obterFiltroUrl: obterFiltroUrl,
                })}
              />
            </div>
          )}
        </div>
      </ListGroupItem>
    );
  };

  const renderSideMenu = () => {
    return (
      <>
        <ListGroup flush style={{ marginBottom: 0, marginTop: 20 }}>
          <ListGroupItem
            action
            className={activeTab == 1 ? 'active' : ''}
            onClick={() => handelToggleTab(TABS.RESUMO)}
            style={{
              cursor: 'pointer',
              border: 'none',
              paddingLeft: '10px',
              color: activeTab == 1 ? undefined : 'var(--primary)',
              fontWeight: 700,
              fontSize: '16px',
              paddingBottom: 6,
            }}
          >
            {capitalizeFirstLetter(intl.formatMessage({ id: 'label.resumo' }))}
          </ListGroupItem>
        </ListGroup>

        {
          <PanelCollapse
            header={capitalizeFirstLetter(
              intl.formatMessage({ id: 'iniciacao' })
            )}
            open={primeiraSecaoAberta === 'iniciacao'}
            ehAccordionDrawer={true}
          >
            <ListGroup flush>
              <>
                {shouldDisplayField('tap') &&
                  renderMenuItem(
                    capitalizeFirstLetter(resources.tap),
                    () => handelToggleTab(TABS.TERMOABERTURA),
                    activeTab == TABS.TERMOABERTURA,
                    false,
                    null,
                    true,
                    !isEditableAttributes
                  )}
                {shouldDisplayField('canvas') &&
                  renderMenuItem(
                    capitalizeFirstLetter(intl.formatMessage({ id: 'canvas' })),
                    () => openCanvasModal(),
                    false,
                    false,
                    null,
                    true,
                    !isEditableAttributes
                  )}
                {utilizaAnaliseStakeholders &&
                  shouldDisplayField('analiseStakeholders') &&
                  renderMenuItem(
                    capitalizeFirstLetter(
                      intl.formatMessage({ id: 'analiseStakeholders' })
                    ),
                    () => handleTabAnaliseStakeholders(),
                    false,
                    false,
                    null,
                    true,
                    !isEditableAttributes
                  )}
                {renderMenuItem(
                  <FormattedMessage
                    id="label.ficha"
                    values={{ ficha: resources.ficha }}
                  />,
                  () => handleFichaClick({ id: entityId }),
                  false,
                  false,
                  null,
                  true,
                  !isEditableAttributes
                )}
              </>
            </ListGroup>
          </PanelCollapse>
        }

        {
          <PanelCollapse
            header={capitalizeFirstLetter(
              intl.formatMessage({ id: 'planejamento' })
            )}
            open={primeiraSecaoAberta === 'planejamento'}
            ehAccordionDrawer={true}
          >
            <ListGroup flush>
              {shouldDisplayField('eapGrafica') &&
                renderMenuItem(
                  capitalizeFirstLetter(
                    intl.formatMessage({ id: 'eapGrafica' })
                  ),
                  () => handleTabEAP(),
                  false,
                  true,
                  'eap',
                  true,
                  !isEditableAttributes
                )}
              {shouldDisplayField('matrizResponsabilidades') &&
                renderMenuItem(
                  capitalizeFirstLetter(
                    intl.formatMessage({ id: 'matrizResponsabilidades' })
                  ),
                  () =>
                    handleMatrizResponsabilidadesClick(),
                  false,
                  true,
                  'matrizResponsabilidades',
                  true,
                  !isEditableAttributes
                )}
              {shouldDisplayField('planoDeAcao') &&
                renderMenuItem(
                  capitalizeFirstLetter(resources.planodeacao),
                  () =>
                    handlePlanoAcaoClick({
                      id: entityId,
                      tipo: { id: TIPO_ITENS.PROJETO },
                    }),
                  false,
                  true,
                  'planoAcao',
                  true,
                  !isEditableAttributes
                )}
              {renderMenuItem(
                capitalizeFirstLetter(
                  intl.formatMessage({ id: 'indicadoresEParametros' })
                ),
                () => handelToggleTab(TABS.INDICADORESEPARAMETROS),
                activeTab == TABS.INDICADORESEPARAMETROS,
                false,
                null,
                true,
                !isEditable
              )}
              {permitirDigitacaoValores &&
                renderMenuItem(
                  capitalizeFirstLetter(
                    intl.formatMessage(
                      { id: 'valores' },
                      { valores: resources.valores }
                    )
                  ),
                  () =>
                    handleValueClick({
                      id: entityId,
                      tipo: { id: TIPO_ITENS.PROJETO },
                    }),
                  false,
                  true,
                  'valores',
                  true,
                  !isEditableAttributes
                )}
            </ListGroup>
          </PanelCollapse>
        }

        {
          <PanelCollapse
            header={capitalizeFirstLetter(
              intl.formatMessage({ id: 'execucaoEMonitoramento' })
            )}
            open={primeiraSecaoAberta === 'execucaoMonitoramento'}
            ehAccordionDrawer={true}
          >
            <ListGroup flush>
              {renderMenuItem(
                capitalizeFirstLetter(resources.statusreport),
                () => handelToggleTab(TABS.STATUSREPORT),
                activeTab == TABS.STATUSREPORT,
                false,
                null,
                true,
                !isEditableAttributes
              )}
              {canCreateLessons &&
                renderMenuItem(
                  capitalizeFirstLetter(
                    intl.formatMessage({ id: 'licoesAprendidas' })
                  ),
                  () => handelToggleTab(TABS.LICOES_APRENDIDAS),
                  activeTab == TABS.LICOES_APRENDIDAS,
                  false,
                  null,
                  true,
                  !isEditableAttributes
                )}
              {renderMenuItem(
                capitalizeFirstLetter(resources.relacionamentos),
                () =>
                  handleRelacionamentosClick({
                    id: entityId,
                    tipo: { id: TIPO_ITENS.PROJETO },
                  }),
                false,
                true,
                'relacionamentos',
                true,
                !isEditableAttributes
              )}
              {exibirPowerBI &&
                renderMenuItem(
                  <FormattedMessage id="bi" />,
                  () =>
                    handleRelatorioBIClick({
                      id: entityId,
                      tipo: { id: TIPO_ITENS.PROJETO },
                    }),
                  false,
                  false,
                  null,
                  true,
                  !isEditableAttributes
                )}
              {possuiAddonContratosConvenios &&
                renderMenuItem(
                  capitalizeFirstLetter(
                    intl.formatMessage({ id: 'movimentacaoFinanceira' })
                  ),
                  () => handelToggleTab(TABS.MOVIMENTACAOFINANCEIRA),
                  activeTab == TABS.MOVIMENTACAOFINANCEIRA,
                  false,
                  null,
                  true,
                  !isEditableAttributes
                )}
            </ListGroup>
          </PanelCollapse>
        }

        {ehProjetoConcluidoCancelado && (
          <PanelCollapse
            header={capitalizeFirstLetter(
              intl.formatMessage({ id: 'encerramento' })
            )}
            open={primeiraSecaoAberta === 'encerramento'}
            ehAccordionDrawer={true}
          >
            <ListGroup flush>
              {renderMenuItem(
                capitalizeFirstLetter(
                  intl.formatMessage({ id: 'termoEncerramento' })
                ),
                () => handelToggleTab(TABS.TERMOENCERRAMENTO),
                activeTab == TABS.TERMOENCERRAMENTO,
                false,
                null,
                true,
                !isEditable
              )}
            </ListGroup>
          </PanelCollapse>
        )}

        <PanelCollapse
          header={capitalizeFirstLetter(
            intl.formatMessage({ id: 'governancaEControle' })
          )}
          open={primeiraSecaoAberta === 'governancaControle'}
          ehAccordionDrawer={true}
        >
          <ListGroup flush>
            {permitirDigitacaoValores &&
              renderMenuItem(
                <FormattedMessage id="label.consolidacao" />,
                () => handelToggleTab(TABS.CONSOLIDACAO),
                activeTab == TABS.CONSOLIDACAO,
                false,
                null,
                true,
                !isEditableAttributes
              )}
            {renderMenuItem(
              <FormattedMessage id="label.permissoes" />,
              () => handelToggleTab(TABS.PERMISSOES),
              activeTab == TABS.PERMISSOES,
              false,
              null,
              true,
              !isEditableAttributes
            )}
            {renderMenuItem(
              <FormattedMessage id="label.comentarios" />,
              () => handleComentarioClick(),
              false,
              false,
              null,
              true,
              !isEditableAttributes
            )}
            {renderMenuItem(
              <FormattedMessage id="label.mensagens" />,
              () =>
                handleMensagemClick({
                  id: entityId,
                  tipo: { id: TIPO_ITENS.PROJETO },
                }),
              false,
              false,
              null,
              true,
              !isEditableAttributes
            )}
            {renderMenuItem(
              <FormattedMessage id="configuracoes" />,
              () => handelToggleTab(TABS.CONFIGURACOES),
              activeTab == TABS.CONFIGURACOES,
              false,
              null,
              true,
              !isEditableAttributes
            )}
            {renderMenuItem(
              <FormattedMessage id="label.historico" />,
              () => handelToggleTab(TABS.HISTORICO),
              activeTab == TABS.HISTORICO,
              false,
              null,
              true,
              !isEditableAttributes
            )}
          </ListGroup>
        </PanelCollapse>
      </>
    );
  };

  const renderContent = () => {
    if (activeTab == TABS.RESUMO)
      return (
        <ResumoStageGate
          model={model}
          selectedEtapa={selectedEtapa}
          key={selectedIndex}
          shouldDisplayField={shouldDisplayField}
          isFieldRequired={isFieldRequired}
          isEditable={isEditable}
          isFieldEditable={isFieldEditable}
          allAnexosConfigs={allAnexosConfigs}
        />
      );
    else if (
      activeTab == TABS.CONSOLIDACAO &&
      permitirDigitacaoValores &&
      id &&
      id > 0
    )
      return (
        <Consolidacao
          disabled={
            model.getModel('permissions').value
              ? !model.getModel('permissions').getModel('allowEdit').value
              : false
          }
          model={model.getModel('consolidacao')}
          tipoItemId={TIPO_ITENS.PROJETO}
          id={id}
        />
      );
    else if (activeTab == TABS.PERMISSOES && id && id > 0)
      return (
        <Permissoes
          model={model}
          id={id}
          url="/ItemPermissao"
          action="AtualizarTipoAcesso"
          actionPermission="ObterItemPermissions"
          parameters={{
            itemId: entityId,
            tipoItemId: TIPO_ITENS.PROJETO,
            url: '/ItemPermissao',
          }}
        />
      );
    else if (activeTab == TABS.CONFIGURACOES && id && id > 0)
      return (
        <Configuracao
          modelGrafico={model}
          model={model.getModel('configuracao')}
          utilizarGestaoMudancas={
            model.getModel('configuracaoGeral').value &&
            model.getModel('configuracaoGeral').value.utilizarGestaoMudancas
          }
          utilizarValoresPrevios={
            model.getModel('configuracaoGeral').value &&
            model.getModel('configuracaoGeral').value.utilizarValoresPrevios
          }
          tipoItemId={TIPO_ITENS.PROJETO}
          id={id}
          exibirCorDoFarol={rest.exibirCorDoFarol}
        />
      );
    else if (activeTab == TABS.HISTORICO && id && id > 0)
      return (
        <Panel>
          <Historico url={`/Projeto/ObterHistorico`} id={id} />
        </Panel>
      );
    else if (activeTab == TABS.STATUSREPORT && id && id > 0)
      return (
        <StatusReportList
          url="/ItemPermissao"
          actionPermission="ObterItemPermissions"
          id={id}
        />
      );
    else if (activeTab == TABS.LICOES_APRENDIDAS && id && id > 0)
      return (
        <Panel>
          <LicoesAprendidasList itemId={id} />
        </Panel>
      );
    else if (activeTab == TABS.INDICADORESEPARAMETROS && id && id > 0)
      return (
        <IndicadoresParametros
          url={`/Projeto/ObterIndicadoresParametros`}
          id={id}
        />
      );
    else if (activeTab == TABS.TERMOABERTURA && id && id > 0)
      return <TermoAbertura model={model} id={id} />;
    else if (activeTab == TABS.CESTAINDICADORES && id && id > 0)
      return <CestaIndicadores itemId={id} />;
    else if (
      activeTab == TABS.TERMOENCERRAMENTO &&
      ehProjetoConcluidoCancelado &&
      id &&
      id > 0
    )
      return <TermoEncerramento model={model} id={id} />;
    else if (
      activeTab == TABS.MOVIMENTACAOFINANCEIRA &&
      possuiAddonContratosConvenios &&
      id &&
      id > 0
    )
      return (
        <>
          <Panel
            header={capitalizeFirstLetter(
              intl.formatMessage({ id: 'movimentacaoFinanceira' })
            )}
          >
            <Row>
              <Col md={12}>
                <GraficoMovimentacaoFinanceira projetoId={id} />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <MovimentacaoFinanceiraListView projetoId={id} />
              </Col>
            </Row>
          </Panel>
        </>
      );
  };

  const handleNextCallback = (
    newStep,
    setActiveStepIndex,
    newIndex,
  ) => {
    if (newIndex > currentStageIndex) return;
    setSelectedIndex(newIndex);
    setActiveStepIndex(newIndex);
  };

  const handleBackCallback = (newStep) => {
    setSelectedIndex(newStep - 1);
  };

  return (
    <Suspense fallback={<LoadingScreen />}>
      <div>
        <Stepper
          hideButtons={true}
          initialStepIndex={currentStageIndex}
          nextCallback={handleNextCallback}
          backCallback={handleBackCallback}
          canClickOnStep={true}
          steps={etapas.map((e) => ({
            titulo: intl.formatMessage({ id: 'estagio' }),
            descricao: e.nome,
            showBadge: e.statusEtapa?.id === 1,
            badgeText: intl.formatMessage({ id: 'pendente' }),
          }))}
          getSetActiveStep={handleGetSetActive}
          confirmNextStep={true}
        >
          <Row>
            <Col md={4} lg={3}>
              <Panel>
                <div
                  style={{
                    maxHeight: 'calc(100vh - 200px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    paddingRight: '8px',
                    marginRight: '-8px',
                  }}
                >
                  {renderSideMenu()}
                </div>
              </Panel>
            </Col>
            <Col Col lg={9} md={8}>
              {renderContent()}
            </Col>
          </Row>
        </Stepper>
      </div>
    </Suspense>
  );
};

export default StageGateEdit;
